// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\RepairableSpareParts.jsx

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { Context } from "../store/appContext";
import getApiBaseUrl from "../utils/api";

import "../styles/repairableSpareParts.css";


const API_URL = getApiBaseUrl().replace(/\/$/, "");
const DEFAULT_PAGE_SIZE = 25;

const OPERATIONAL_STATUS_LABELS = {
  PENDIENTE_DE_ENVIAR: "Pendiente de enviar",
  ENVIADO: "Enviado",
  RECEPCIONADO: "Recepcionado",
  INCIDENCIA: "Incidencia",
};

const formatDate = (value) => {
  if (!value) return "—";

  const normalized = String(value).slice(0, 10);
  const [year, month, day] = normalized.split("-");

  if (!year || !month || !day) return String(value);

  return `${day}/${month}/${year}`;
};

const dateInput = (value) =>
  value ? String(value).slice(0, 10) : "";

const formatMoney = (value, currency = "EUR") => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) return String(value);

  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: currency || "EUR",
  }).format(amount);
};

const errorMessage = (payload, fallback) => {
  if (typeof payload?.error === "string") return payload.error;
  if (payload?.error?.message) return payload.error.message;
  return payload?.msg || payload?.message || fallback;
};

const requestJson = async ({
  token,
  actions,
  path,
  method = "GET",
  params,
  body,
  signal,
  fallback,
}) => {
  const search = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      search.append(key, value);
    }
  });

  const query = search.toString();
  const response = await fetch(
    `${API_URL}${path}${query ? `?${query}` : ""}`,
    {
      method,
      signal,
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined
          ? { "Content-Type": "application/json" }
          : {}),
      },
      ...(body !== undefined
        ? { body: JSON.stringify(body) }
        : {}),
    },
  );

  if (response.status === 401 && actions?.handleApiError) {
    await actions.handleApiError(response);
  }

  const payload = await response.json().catch(() => null);

  if (!response.ok || payload?.success !== true) {
    throw new Error(
      errorMessage(
        payload,
        fallback || "No se pudo completar la operación",
      ),
    );
  }

  return payload?.data ?? null;
};

const normalizeOrmaRows = (data) => {
  const rows = data?.items || data?.rows || data?.results || [];
  return Array.isArray(rows) ? rows : [];
};

const getOrmaDescription = (row) =>
  row?.master_description ||
  row?.visor_description ||
  row?.description ||
  row?.material_description ||
  "Sin descripción";

const previewStatus = ({
  incident,
  sentAt,
  receivedAt,
}) => {
  if (incident) return "INCIDENCIA";
  if (receivedAt) return "RECEPCIONADO";
  if (sentAt) return "ENVIADO";
  return "PENDIENTE_DE_ENVIAR";
};

const StatusBadge = ({ status }) => (
  <span
    className={`repairable-status repairable-operational-status-${String(
      status || "",
    ).toLowerCase()}`}
  >
    {OPERATIONAL_STATUS_LABELS[status] || status || "—"}
  </span>
);

const SupplierInput = ({
  id,
  value,
  onChange,
  token,
  actions,
  placeholder = "Escribir o seleccionar proveedor",
  inputRef,
}) => {
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    const query = value.trim();
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const data = await requestJson({
          token,
          actions,
          path: "/api/repairable-spare-parts/suppliers",
          params: {
            q: query,
            limit: 12,
          },
          signal: controller.signal,
          fallback: "No se pudieron cargar proveedores",
        });

        setSuggestions(
          Array.isArray(data?.items) ? data.items : [],
        );
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setSuggestions([]);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [value, token, actions]);

  const listId = `${id}_suggestions`;

  return (
    <>
      <input
        ref={inputRef}
        id={id}
        list={listId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete="off"
      />
      <datalist id={listId}>
        {suggestions.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
    </>
  );
};

const InlineCreatePanel = ({
  token,
  actions,
  onCreated,
  onCancel,
}) => {
  const [materialSearch, setMaterialSearch] = useState("");
  const [materialRows, setMaterialRows] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [supplierName, setSupplierName] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [sentAt, setSentAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const query = materialSearch.trim();

    if (selectedMaterial?.material_id === query) {
      setMaterialRows([]);
      return undefined;
    }

    if (query.length < 2) {
      setMaterialRows([]);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setMaterialLoading(true);
      setError("");

      try {
        const data = await requestJson({
          token,
          actions,
          path: "/api/catalog/orma-catalog/search",
          params: {
            q: query,
            page: 1,
            per_page: 10,
            sort_by: "material_id",
            sort_order: "asc",
          },
          signal: controller.signal,
          fallback: "No se pudo buscar el material ORMA",
        });

        setMaterialRows(normalizeOrmaRows(data));
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setMaterialRows([]);
          setError(requestError.message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setMaterialLoading(false);
        }
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [materialSearch, selectedMaterial?.material_id, token, actions]);

  const selectMaterial = (row) => {
    setSelectedMaterial(row);
    setMaterialSearch(String(row?.material_id || ""));
    setMaterialRows([]);
    setError("");
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!selectedMaterial?.material_id) {
      setError("Selecciona un material del catálogo ORMA");
      return;
    }

    const body = {
      material_id: selectedMaterial.material_id,
    };

    if (selectedMaterial.branch_name) {
      body.branch_name = selectedMaterial.branch_name;
    }

    if (selectedMaterial.warehouse_id) {
      body.warehouse_id = selectedMaterial.warehouse_id;
    }

    if (supplierName.trim()) {
      body.supplier_name = supplierName.trim();
    }

    if (sentAt) {
      body.sent_at = sentAt;
    }

    body.serial_number = serialNumber.trim() || null;

    setSubmitting(true);
    setError("");

    try {
      const created = await requestJson({
        token,
        actions,
        path: "/api/repairable-spare-parts/operational",
        method: "POST",
        body,
        fallback: "No se pudo crear el expediente",
      });

      actions?.showToast?.("Expediente creado", "success");
      setSerialNumber("");
      await onCreated(created);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const status = previewStatus({
    incident: false,
    sentAt,
    receivedAt: "",
  });

  return (
    <form
      className="repairable-card repairable-inline-create repairable-inline-create-simple"
      onSubmit={submit}
    >
      <div className="repairable-inline-create-head">
        <div>
          <span className="repairable-eyebrow">
            NUEVO EXPEDIENTE
          </span>
          <h2>Alta rápida</h2>
        </div>
        <StatusBadge status={status} />
      </div>

      <div className="repairable-inline-create-grid repairable-inline-create-grid-simple">
        <div className="repairable-field repairable-inline-material">
          <label htmlFor="repairable_inline_material">
            ID material
          </label>
          <div className="repairable-search-wrap">
            <i className="bi bi-search" aria-hidden="true"></i>
            <input
              id="repairable_inline_material"
              value={materialSearch}
              onChange={(event) => {
                setMaterialSearch(event.target.value);
                setSelectedMaterial(null);
                setError("");
              }}
              autoComplete="off"
              placeholder="Buscar ID o descripción"
              aria-expanded={materialRows.length > 0}
            />
          </div>

          {materialLoading && (
            <small className="repairable-inline-help">
              Buscando materiales...
            </small>
          )}

          {materialRows.length > 0 && (
            <div className="repairable-inline-material-results">
              {materialRows.map((row, index) => (
                <button
                  key={`${row?.material_id || "material"}-${row?.branch_name || ""}-${row?.warehouse_id || ""}-${index}`}
                  type="button"
                  onClick={() => selectMaterial(row)}
                >
                  <strong>{row?.material_id || "Sin ID"}</strong>
                  <span>{getOrmaDescription(row)}</span>
                  {(row?.branch_name || row?.warehouse_id) && (
                    <small>
                      {[row?.branch_name, row?.warehouse_id]
                        .filter(Boolean)
                        .join(" · ")}
                    </small>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="repairable-field repairable-inline-description">
          <label htmlFor="repairable_inline_description">
            Descripción
          </label>
          <input
            id="repairable_inline_description"
            value={
              selectedMaterial
                ? getOrmaDescription(selectedMaterial)
                : ""
            }
            readOnly
            placeholder="Automática desde ORMA"
          />
        </div>

        <div className="repairable-field">
          <label htmlFor="repairable_inline_serial_number">
            Número de serie
          </label>
          <input
            id="repairable_inline_serial_number"
            type="text"
            value={serialNumber}
            maxLength={255}
            onChange={(event) => {
              setSerialNumber(event.target.value);
              setError("");
            }}
            placeholder="Opcional"
          />
        </div>

        <div className="repairable-field">
          <label htmlFor="repairable_inline_supplier">
            Proveedor
          </label>
          <SupplierInput
            id="repairable_inline_supplier"
            value={supplierName}
            onChange={setSupplierName}
            token={token}
            actions={actions}
          />
        </div>

        <div className="repairable-field">
          <label htmlFor="repairable_inline_sent_at">
            Fecha de envío
          </label>
          <input
            id="repairable_inline_sent_at"
            type="date"
            value={sentAt}
            onChange={(event) => {
              setSentAt(event.target.value);
              setError("");
            }}
          />
        </div>
      </div>

      {error && (
        <div className="repairable-alert repairable-alert-error" role="alert">
          {error}
        </div>
      )}

      <div className="repairable-inline-create-actions">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting || !selectedMaterial}
        >
          {submitting ? "Creando..." : "Crear expediente"}
        </button>
      </div>
    </form>
  );
};

const EditModal = ({
  item,
  role,
  token,
  actions,
  onSaved,
  onClose,
}) => {
  const isAdmin = role === "admin";
  const initialIncident =
    item.operational_status === "INCIDENCIA";
  const firstInputRef = useRef(null);

  const [form, setForm] = useState({
    supplier_name: item.supplier_name || "",
    serial_number: item.serial_number || "",
    sent_at: dateInput(item.sent_at),
    received_at: dateInput(item.received_at),
    purchase_order_number:
      item.purchase_order_number || "",
    final_amount:
      item.final_amount === null ||
      item.final_amount === undefined
        ? ""
        : String(item.final_amount),
    incident: initialIncident,
    incident_description:
      item.incident_description || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm({
      supplier_name: item.supplier_name || "",
      serial_number: item.serial_number || "",
      sent_at: dateInput(item.sent_at),
      received_at: dateInput(item.received_at),
      purchase_order_number:
        item.purchase_order_number || "",
      final_amount:
        item.final_amount === null ||
        item.final_amount === undefined
          ? ""
          : String(item.final_amount),
      incident:
        item.operational_status === "INCIDENCIA",
      incident_description:
        item.incident_description || "",
    });
    setError("");
  }, [item]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.classList.add("modal-open");
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      firstInputRef.current?.focus();
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("modal-open");
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose, saving]);

  const setField = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
    setError("");
  };

  const save = async (event) => {
    event.preventDefault();

    if (form.received_at && !form.sent_at) {
      setError("La recepción requiere una fecha de envío");
      return;
    }

    if (
      form.sent_at &&
      form.received_at &&
      form.received_at < form.sent_at
    ) {
      setError("La recepción no puede ser anterior al envío");
      return;
    }

    if (
      form.incident &&
      !form.incident_description.trim()
    ) {
      setError("Describe brevemente la incidencia");
      return;
    }

    if (
      isAdmin &&
      form.final_amount !== "" &&
      (
        !Number.isFinite(Number(form.final_amount)) ||
        Number(form.final_amount) < 0
      )
    ) {
      setError("El coste debe ser un importe válido");
      return;
    }

    const body = {
      supplier_name: form.supplier_name.trim() || null,
      serial_number: form.serial_number.trim() || null,
      sent_at: form.sent_at || null,
      received_at: form.received_at || null,
      purchase_order_number:
        form.purchase_order_number.trim() || null,
      incident: form.incident,
      incident_description:
        form.incident_description.trim() || null,
    };

    if (isAdmin) {
      body.final_amount =
        form.final_amount === ""
          ? null
          : Number(form.final_amount);
    }

    setSaving(true);
    setError("");

    try {
      const updated = await requestJson({
        token,
        actions,
        path: `/api/repairable-spare-parts/${item.id}/operational`,
        method: "PATCH",
        body,
        fallback: "No se pudo guardar el expediente",
      });

      actions?.showToast?.("Expediente actualizado", "success");
      await onSaved(updated);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const currentPreview = previewStatus({
    incident: form.incident,
    sentAt: form.sent_at,
    receivedAt: form.received_at,
  });

  const closeFromOverlay = (event) => {
    if (event.target === event.currentTarget && !saving) {
      onClose();
    }
  };

  return (
    <div
      className="repairable-modal-overlay repairable-edit-modal-overlay"
      role="presentation"
      onMouseDown={closeFromOverlay}
    >
      <form
        className="repairable-modal repairable-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="repairable_edit_modal_title"
        onSubmit={save}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="repairable-modal-header">
          <div>
            <span className="repairable-eyebrow">
              EDICIÓN OPERATIVA
            </span>
            <h2 id="repairable_edit_modal_title">
              Editar {item.case_number}
            </h2>
          </div>
          <button
            type="button"
            className="repairable-icon-button"
            onClick={onClose}
            disabled={saving}
            aria-label="Cerrar edición"
          >
            <i className="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </header>

        <div className="repairable-modal-body">
          <div className="repairable-edit-modal-summary">
            <div>
              <span>Material</span>
              <strong>{item.material_id}</strong>
              <p>{item.material_description || "Sin descripción"}</p>
            </div>
            <StatusBadge status={currentPreview} />
          </div>

          <div className="repairable-edit-modal-grid">
            <div className="repairable-field repairable-edit-modal-wide">
              <label htmlFor={`edit_supplier_${item.id}`}>
                Proveedor
              </label>
              <SupplierInput
                id={`edit_supplier_${item.id}`}
                value={form.supplier_name}
                onChange={(value) =>
                  setField("supplier_name", value)
                }
                token={token}
                actions={actions}
                placeholder="Buscar o escribir proveedor"
                inputRef={firstInputRef}
              />
            </div>

            <div className="repairable-field">
              <label htmlFor={`edit_serial_${item.id}`}>
                Número de serie
              </label>
              <input
                id={`edit_serial_${item.id}`}
                type="text"
                value={form.serial_number}
                maxLength={255}
                onChange={(event) =>
                  setField("serial_number", event.target.value)
                }
                placeholder="Opcional"
              />
            </div>

            <div className="repairable-field">
              <label htmlFor={`edit_sent_${item.id}`}>
                Fecha de envío
              </label>
              <input
                id={`edit_sent_${item.id}`}
                type="date"
                value={form.sent_at}
                onChange={(event) =>
                  setField("sent_at", event.target.value)
                }
              />
            </div>

            <div className="repairable-field">
              <label htmlFor={`edit_received_${item.id}`}>
                Fecha de recepción
              </label>
              <input
                id={`edit_received_${item.id}`}
                type="date"
                min={form.sent_at || undefined}
                value={form.received_at}
                onChange={(event) =>
                  setField("received_at", event.target.value)
                }
              />
            </div>

            <div className="repairable-field">
              <label htmlFor={`edit_order_${item.id}`}>
                N.º de pedido
              </label>
              <input
                id={`edit_order_${item.id}`}
                value={form.purchase_order_number}
                onChange={(event) =>
                  setField(
                    "purchase_order_number",
                    event.target.value,
                  )
                }
                placeholder="Opcional"
              />
            </div>

            {isAdmin && (
              <div className="repairable-field">
                <label htmlFor={`edit_cost_${item.id}`}>
                  Coste de reparación
                </label>
                <div className="repairable-inline-money">
                  <input
                    id={`edit_cost_${item.id}`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.final_amount}
                    onChange={(event) =>
                      setField(
                        "final_amount",
                        event.target.value,
                      )
                    }
                    placeholder="0,00"
                  />
                  <span>EUR</span>
                </div>
              </div>
            )}

            <div className="repairable-field repairable-edit-modal-wide">
              <label htmlFor={`edit_incident_${item.id}`}>
                Incidencia
              </label>
              <label className="repairable-switch-line">
                <input
                  id={`edit_incident_${item.id}`}
                  type="checkbox"
                  checked={form.incident}
                  onChange={(event) =>
                    setField("incident", event.target.checked)
                  }
                />
                <span>
                  {form.incident
                    ? "Incidencia activa"
                    : "Sin incidencia"}
                </span>
              </label>
            </div>

            {(form.incident || initialIncident) && (
              <div className="repairable-field repairable-edit-modal-wide">
                <label htmlFor={`edit_incident_note_${item.id}`}>
                  Descripción de la incidencia
                </label>
                <textarea
                  id={`edit_incident_note_${item.id}`}
                  rows="3"
                  value={form.incident_description}
                  onChange={(event) =>
                    setField(
                      "incident_description",
                      event.target.value,
                    )
                  }
                  placeholder={
                    form.incident
                      ? "Indica brevemente el problema"
                      : "Nota de resolución opcional"
                  }
                />
              </div>
            )}
          </div>

          {error && (
            <div
              className="repairable-alert repairable-alert-error repairable-edit-modal-alert"
              role="alert"
            >
              {error}
            </div>
          )}
        </div>

        <footer className="repairable-modal-footer">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </footer>
      </form>
    </div>
  );
};

const DeleteCaseModal = ({
  target,
  actions,
  onDeleted,
  onClose,
}) => {
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const passwordRef = useRef(null);

  useEffect(() => {
    if (!target) return;

    setPassword("");
    setReason("");
    setSubmitting(false);
    setError("");

    const previousOverflow = document.body.style.overflow;
    document.body.classList.add("modal-open");
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      passwordRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.classList.remove("modal-open");
      document.body.style.overflow = previousOverflow;
    };
  }, [target]);

  useEffect(() => {
    if (!target) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [target, submitting, onClose]);

  if (!target) return null;

  const close = () => {
    if (submitting) return;
    setPassword("");
    setReason("");
    setError("");
    onClose();
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!password) {
      setError("Introduce tu contraseña actual.");
      return;
    }

    if (!reason.trim()) {
      setError("Indica el motivo de eliminación.");
      return;
    }

    setSubmitting(true);
    setError("");

    const result = await actions.deleteRepairableSparePart(
      target.id,
      {
        password,
        reason: reason.trim(),
      },
    );

    if (!result.success) {
      setSubmitting(false);
      setError(
        typeof result.error === "string" && result.error.trim()
          ? result.error
          : "No se pudo eliminar el expediente.",
      );
      return;
    }

    setPassword("");
    setReason("");
    setError("");
    await onDeleted(target);
  };

  const closeFromOverlay = (event) => {
    if (event.target === event.currentTarget) {
      close();
    }
  };

  return (
    <div
      className="repairable-modal-overlay repairable-delete-case-overlay"
      role="presentation"
      onMouseDown={closeFromOverlay}
    >
      <form
        className="repairable-modal repairable-modal-danger repairable-delete-case-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="repairable_delete_case_title"
        onSubmit={submit}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="repairable-modal-header">
          <div>
            <span className="repairable-eyebrow">ELIMINACIÓN LÓGICA</span>
            <h2 id="repairable_delete_case_title">
              Eliminar {target.case_number}
            </h2>
          </div>
          <button
            type="button"
            className="repairable-icon-button"
            onClick={close}
            disabled={submitting}
            aria-label="Cerrar eliminación"
          >
            <i className="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </header>

        <div className="repairable-modal-body">
          <div className="repairable-delete-case-summary">
            <div>
              <span>Material</span>
              <strong>{target.material_id || "—"}</strong>
              <p>{target.material_description || "Sin descripción"}</p>
            </div>
            <div>
              <span>N.º de serie</span>
              <strong>{target.serial_number || "—"}</strong>
            </div>
          </div>

          <p className="repairable-delete-case-warning">
            El expediente quedará fuera de los listados operativos y se
            conservará para trazabilidad. Esta acción no realiza un borrado
            físico.
          </p>

          <div className="repairable-delete-case-fields">
            <div className="repairable-field">
              <label htmlFor="repairable_delete_case_password">
                Contraseña actual
              </label>
              <input
                ref={passwordRef}
                id="repairable_delete_case_password"
                type="password"
                autoComplete="current-password"
                value={password}
                disabled={submitting}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setError("");
                }}
              />
            </div>

            <div className="repairable-field">
              <label htmlFor="repairable_delete_case_reason">
                Motivo de eliminación
              </label>
              <textarea
                id="repairable_delete_case_reason"
                rows="4"
                maxLength={2000}
                value={reason}
                disabled={submitting}
                onChange={(event) => {
                  setReason(event.target.value);
                  setError("");
                }}
                placeholder="Motivo obligatorio"
              />
              <small className="repairable-delete-case-counter">
                {reason.length} / 2000
              </small>
            </div>
          </div>

          {error && (
            <div
              className="repairable-alert repairable-alert-error repairable-delete-case-error"
              role="alert"
            >
              {error}
            </div>
          )}
        </div>

        <footer className="repairable-modal-footer">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={close}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-danger"
            disabled={submitting}
          >
            {submitting ? "Eliminando..." : "Eliminar expediente"}
          </button>
        </footer>
      </form>
    </div>
  );
};

const RepairableSpareParts = () => {
  const { store, actions } = useContext(Context);
  const navigate = useNavigate();
  const token = store.token || localStorage.getItem("token");
  const role = String(
    store.role || localStorage.getItem("role") || "",
  ).toLowerCase();

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
    per_page: DEFAULT_PAGE_SIZE,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [operationalStatus, setOperationalStatus] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [sentFrom, setSentFrom] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [receivedFrom, setReceivedFrom] = useState("");
  const [receivedTo, setReceivedTo] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reloadVersion, setReloadVersion] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      350,
    );

    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    operationalStatus,
    supplierName,
    sentFrom,
    sentTo,
    receivedFrom,
    receivedTo,
    perPage,
  ]);

  const loadList = useCallback(
    async (signal) => {
      if (!token) return;

      setLoading(true);
      setError("");

      try {
        const data = await requestJson({
          token,
          actions,
          path: "/api/repairable-spare-parts/operational",
          signal,
          params: {
            q: debouncedSearch,
            operational_status: operationalStatus,
            supplier_name: supplierName.trim(),
            sent_from: sentFrom,
            sent_to: sentTo,
            received_from: receivedFrom,
            received_to: receivedTo,
            page,
            per_page: perPage,
            sort_by: sortBy,
            sort_dir: sortDir,
          },
          fallback: "No se pudieron cargar los expedientes",
        });

        const rows = Array.isArray(data?.items)
          ? data.items
          : [];
        const meta = data?.pagination || {};

        setItems(rows);
        setPagination({
          page: Number(meta.page || page),
          pages: Math.max(1, Number(meta.pages || 1)),
          total: Number(meta.total || 0),
          per_page: Number(meta.per_page || perPage),
        });
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setItems([]);
          setError(requestError.message);
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [
      token,
      actions,
      debouncedSearch,
      operationalStatus,
      supplierName,
      sentFrom,
      sentTo,
      receivedFrom,
      receivedTo,
      page,
      perPage,
      sortBy,
      sortDir,
      reloadVersion,
    ],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadList(controller.signal);

    return () => controller.abort();
  }, [loadList]);

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setOperationalStatus("");
    setSupplierName("");
    setSentFrom("");
    setSentTo("");
    setReceivedFrom("");
    setReceivedTo("");
    setPage(1);
  };

  const activeFilterCount = [
    operationalStatus,
    supplierName.trim(),
    sentFrom,
    sentTo,
    receivedFrom,
    receivedTo,
  ].filter(Boolean).length;

  const handleCreated = async (created) => {
    setCreateOpen(false);
    setPage(1);
    setEditingItem(created || null);
    setReloadVersion((current) => current + 1);
  };

  const handleSaved = async (updated) => {
    setItems((current) =>
      current.map((item) =>
        item.id === updated.id ? updated : item,
      ),
    );
    setEditingItem(null);
    setReloadVersion((current) => current + 1);
  };

  const handleDeleted = async () => {
    const shouldMoveToPreviousPage =
      items.length === 1 && page > 1;

    setDeleteTarget(null);

    if (shouldMoveToPreviousPage) {
      setPage((current) => Math.max(1, current - 1));
      return;
    }

    setReloadVersion((current) => current + 1);
  };

  const sortHeader = (column, label) => (
    <button
      type="button"
      className="repairable-sort-button"
      onClick={() => {
        if (sortBy === column) {
          setSortDir((current) =>
            current === "asc" ? "desc" : "asc",
          );
        } else {
          setSortBy(column);
          setSortDir("asc");
        }
        setPage(1);
      }}
    >
      {label}
      {sortBy === column &&
        (sortDir === "asc" ? " ▲" : " ▼")}
    </button>
  );

  return (
    <main className="repairable-page">
      <header className="repairable-page-header">
        <div className="repairable-title-block">
          <div className="repairable-title-icon" aria-hidden="true">
            <i className="bi bi-tools"></i>
          </div>
          <div>
            <span className="repairable-eyebrow">
              CONTROL OPERATIVO
            </span>
            <h1>Repuestos enviados a reparar</h1>
            <p>
              Alta, envío, recepción, incidencias y coste desde una sola pantalla.
            </p>
          </div>
        </div>

        <div className="repairable-header-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              setCreateOpen((current) => !current)
            }
            aria-expanded={createOpen}
          >
            <i
              className={
                createOpen
                  ? "bi bi-x-lg"
                  : "bi bi-plus-lg"
              }
              aria-hidden="true"
            ></i>
            {createOpen ? "Cerrar alta" : "Nuevo expediente"}
          </button>
        </div>
      </header>

      {createOpen && (
        <InlineCreatePanel
          token={token}
          actions={actions}
          onCreated={handleCreated}
          onCancel={() => setCreateOpen(false)}
        />
      )}

      <section className="repairable-card repairable-filter-toolbar">
        <div className="repairable-field repairable-search-wrap repairable-toolbar-search">
          <label htmlFor="repairable_search">Buscar</label>
          <i className="bi bi-search" aria-hidden="true"></i>
          <input
            id="repairable_search"
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
            placeholder="Buscar por expediente, ID, descripción o número de serie"
          />
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary repairable-filter-toggle"
          onClick={() =>
            setFiltersOpen((current) => !current)
          }
          aria-expanded={filtersOpen}
          aria-controls="repairable_filter_panel"
        >
          <i className="bi bi-funnel" aria-hidden="true"></i>
          Filtros
          {activeFilterCount > 0 && (
            <span className="repairable-filter-count">
              {activeFilterCount}
            </span>
          )}
          <i
            className={
              filtersOpen
                ? "bi bi-chevron-up"
                : "bi bi-chevron-down"
            }
            aria-hidden="true"
          ></i>
        </button>
      </section>

      {filtersOpen && (
        <section
          id="repairable_filter_panel"
          className="repairable-card repairable-filters repairable-filters-collapsible"
        >
          <div className="repairable-filter-grid repairable-filter-grid-four-state">
            <div className="repairable-field">
              <label htmlFor="repairable_status">
                Estado
              </label>
              <select
                id="repairable_status"
                value={operationalStatus}
                onChange={(event) =>
                  setOperationalStatus(event.target.value)
                }
              >
                <option value="">Todos</option>
                {Object.entries(
                  OPERATIONAL_STATUS_LABELS,
                ).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="repairable-field">
              <label htmlFor="repairable_supplier_filter">
                Proveedor
              </label>
              <SupplierInput
                id="repairable_supplier_filter"
                value={supplierName}
                onChange={setSupplierName}
                token={token}
                actions={actions}
                placeholder="Buscar proveedor"
              />
            </div>

            <div className="repairable-field">
              <label htmlFor="repairable_sent_from">
                Enviado desde
              </label>
              <input
                id="repairable_sent_from"
                type="date"
                value={sentFrom}
                onChange={(event) =>
                  setSentFrom(event.target.value)
                }
              />
            </div>

            <div className="repairable-field">
              <label htmlFor="repairable_sent_to">
                Enviado hasta
              </label>
              <input
                id="repairable_sent_to"
                type="date"
                value={sentTo}
                onChange={(event) =>
                  setSentTo(event.target.value)
                }
              />
            </div>

            <div className="repairable-field">
              <label htmlFor="repairable_received_from">
                Recibido desde
              </label>
              <input
                id="repairable_received_from"
                type="date"
                value={receivedFrom}
                onChange={(event) =>
                  setReceivedFrom(event.target.value)
                }
              />
            </div>

            <div className="repairable-field">
              <label htmlFor="repairable_received_to">
                Recibido hasta
              </label>
              <input
                id="repairable_received_to"
                type="date"
                value={receivedTo}
                onChange={(event) =>
                  setReceivedTo(event.target.value)
                }
              />
            </div>

            <div className="repairable-filter-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={clearFilters}
                disabled={
                  activeFilterCount === 0 && !search
                }
              >
                Limpiar
              </button>
            </div>
          </div>
        </section>
      )}

      {error && (
        <div className="repairable-alert repairable-alert-error" role="alert">
          {error}
        </div>
      )}

      <section className="repairable-card repairable-table-card">
        <div className="repairable-table-meta">
          <span>
            {pagination.total} expediente
            {pagination.total === 1 ? "" : "s"}
          </span>
          {loading && (
            <span className="repairable-loading-dot">
              Actualizando
            </span>
          )}
        </div>

        <div className="repairable-table-scroll repairable-table-scroll-compact">
          <table className="table mb-0 repairable-table repairable-table-four-state repairable-table-compact">
            <colgroup>
              <col className="repairable-col-case" />
              <col className="repairable-col-material" />
              <col className="repairable-col-serial" />
              <col className="repairable-col-status" />
              <col className="repairable-col-order" />
              <col className="repairable-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <div className="repairable-table-heading-stack">
                    {sortHeader("case_number", "Expediente")}
                    {sortHeader("supplier_name", "Proveedor")}
                  </div>
                </th>
                <th>{sortHeader("material_id", "Material")}</th>
                <th>N.º de serie</th>
                <th>
                  <div className="repairable-table-heading-stack">
                    <span>Estado</span>
                    {sortHeader("sent_at", "Envío")}
                    {sortHeader("received_at", "Recepción")}
                  </div>
                </th>
                <th>
                  <div className="repairable-table-heading-stack">
                    {sortHeader(
                      "purchase_order_number",
                      "Pedido",
                    )}
                    {sortHeader("final_amount", "Coste")}
                  </div>
                </th>
                <th className="repairable-actions-column">
                  Acciones
                </th>
              </tr>
            </thead>

            <tbody>
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan="6" className="repairable-empty-cell">
                    No hay expedientes para los filtros seleccionados.
                  </td>
                </tr>
              )}

              {items.map((item) => (
                <tr
                  key={item.id}
                  className={
                    item.operational_status === "INCIDENCIA"
                      ? "repairable-row-incident"
                      : undefined
                  }
                >
                  <td
                    data-label="Expediente"
                    className="repairable-case-cell"
                  >
                    <strong>{item.case_number}</strong>
                    <small title={item.supplier_name || undefined}>
                      Proveedor: {item.supplier_name || "Sin proveedor"}
                    </small>
                  </td>
                  <td
                    data-label="Material"
                    className="repairable-material-cell"
                  >
                    <strong>{item.material_id}</strong>
                    <p
                      className="repairable-material-description"
                      title={item.material_description || undefined}
                    >
                      {item.material_description || "Sin descripción"}
                    </p>
                    {(item.brand || item.model) && (
                      <small>
                        {[item.brand, item.model]
                          .filter(Boolean)
                          .join(" · ")}
                      </small>
                    )}
                  </td>
                  <td
                    data-label="N.º de serie"
                    className="repairable-serial-cell"
                  >
                    <span title={item.serial_number || undefined}>
                      {item.serial_number || "Sin serie"}
                    </span>
                  </td>
                  <td
                    data-label="Estado y seguimiento"
                    className="repairable-status-cell"
                  >
                    <StatusBadge
                      status={item.operational_status}
                    />
                    <div className="repairable-row-metadata">
                      <span>
                        <strong>Envío:</strong> {formatDate(item.sent_at)}
                      </span>
                      <span>
                        <strong>Recepción:</strong>{" "}
                        {formatDate(item.received_at)}
                      </span>
                      <span>
                        <strong>Días fuera:</strong>{" "}
                        {item.days_out ?? 0}
                      </span>
                    </div>
                  </td>
                  <td
                    data-label="Pedido y coste"
                    className="repairable-order-cell"
                  >
                    <strong>
                      {item.purchase_order_number || "Sin pedido"}
                    </strong>
                    <small>
                      Coste:{" "}
                      {role === "admin"
                        ? formatMoney(
                            item.final_amount,
                            item.currency,
                          )
                        : "—"}
                    </small>
                  </td>
                  <td
                    data-label="Acciones"
                    className="repairable-row-actions"
                  >
                    <div className="repairable-row-action-group">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() =>
                          navigate(
                            `/reparable-spare-parts/${item.id}`,
                          )
                        }
                        aria-label={`Ver ficha ${item.case_number}`}
                      >
                        <i className="bi bi-eye" aria-hidden="true"></i>
                        Ver ficha
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm repairable-edit-button"
                        onClick={() => setEditingItem(item)}
                        aria-label={`Editar ${item.case_number}`}
                      >
                        <i
                          className="bi bi-pencil-square"
                          aria-hidden="true"
                        ></i>
                        Editar
                      </button>
                      {role === "admin" && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => setDeleteTarget(item)}
                          aria-label={`Eliminar ${item.case_number}`}
                        >
                          <i
                            className="bi bi-trash3"
                            aria-hidden="true"
                          ></i>
                          Eliminar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="repairable-pagination">
          <div className="repairable-pagination-size">
            <label htmlFor="repairable_per_page">
              Filas
            </label>
            <select
              id="repairable_per_page"
              value={perPage}
              onChange={(event) =>
                setPerPage(Number(event.target.value))
              }
            >
              {[25, 50, 100].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          <div className="repairable-pagination-controls">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() =>
                setPage((current) =>
                  Math.max(1, current - 1),
                )
              }
              disabled={loading || page <= 1}
            >
              Anterior
            </button>
            <span>
              Página <strong>{pagination.page}</strong> de{" "}
              <strong>{pagination.pages}</strong>
            </span>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() =>
                setPage((current) =>
                  Math.min(
                    pagination.pages,
                    current + 1,
                  ),
                )
              }
              disabled={
                loading || page >= pagination.pages
              }
            >
              Siguiente
            </button>
          </div>
        </div>
      </section>

      {editingItem && (
        <EditModal
          item={editingItem}
          role={role}
          token={token}
          actions={actions}
          onSaved={handleSaved}
          onClose={() => setEditingItem(null)}
        />
      )}

      <DeleteCaseModal
        target={deleteTarget}
        actions={actions}
        onDeleted={handleDeleted}
        onClose={() => setDeleteTarget(null)}
      />
    </main>
  );
};

export default RepairableSpareParts;
